import React from 'react';
import { Form, Header, Icon, Popup } from 'semantic-ui-react';

import { useDispatch, useSelector } from 'react-redux';
import accountActions from "actions/account";
import {
    ConfirmationPrompt,
    Modal,
    STATUS_TYPES,
    Notification,
    CloseableContent,
    BannedIcon
} from 'dyl-components';
import { NavLink, useParams } from 'react-router-dom';

import 'shared/ProfileControls.scss';
import LinkToMasterAccountForm from "shared/forms/LinkToMasterAccountForm";
import masterAccountActions from "actions/master_account";
import CustomerPipelineForm from 'shared/forms/CustomerPipelineForm';
import LostForm from 'shared/forms/LostForm';
import ChangeCustomerPipelineForm from './ChangeCustomerPipelineForm';
import { PhoneUtil } from 'utils';
import { ActivityPopup } from 'shared/forms/NewActivity';
import { TextMessageModal } from 'shared/modals';
import TextIcon from 'shared/TextIcon';
import EmailIcon from 'shared/EmailIcon';

const Controls = ({ popupRef, openForm, setOpenForm }) => {
    const {
        phone,
        call_suspended,
        master_account,
        account_type,
        accountBeingUnlinked,
        pipeline,
        isUpdatingPipeline,
        primary_contact_id,
        userPhoneCreds,
        account_name,
        hasEmail
    } = useSelector(state => {
        const mainPhone = state.contact_phone.phone.find(phone => phone.main) || state.contact_phone?.phone[0] || { phone: '' };
        return {
            phone: state.contact_phone.phone.length > 0 ? `${mainPhone?.phone}` : "",            
            call_suspended: mainPhone?.call_suspended,
            master_account: state.account.account.master_account?.id,
            account_type: state.account.account?.account_type,
            accountBeingUnlinked: state.master_account.accountBeingUnlinked,
            pipeline: state.account.account?.customer?.customer_pipeline,
            isUpdatingPipeline: state.customer_pipeline.accountBeingUpdated,
            primary_contact_id: state.account.account?.primary_contact?.id,
            userPhoneCreds: state?.officeView?.creds,
            account_name: state.account.account?.name,
            hasEmail: state.contact_email.email?.length > 0
        }
    });
    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const dispatch = useDispatch();

    const { account_id } = useParams();

    const locationParam = () => `account/${account_id}`;

    const onCloseForm = () => {
        setOpenForm(null);
    }

    const refresh = () => {
        dispatch(accountActions.readAccount(account_id));
    }

    const onClickToDial = () => {
        PhoneUtil.C2D({ payload: { account_id: Number(account_id), destination: phone }, dispatch });
    }

    return (
        <React.Fragment>
            {isPhoneSystemSupported ?
                (
                    !call_suspended ? (
                        <Icon onClick={onClickToDial} disabled={!phone} className='Profile__controls fas fa-phone' link />
                    ) : (
                        <BannedIcon 
                            iconName='fas fa-phone'
                            iconClassName='Profile__controls'
                            banIconSize='tiny'
                        />
                    )
                ) :
                <ActivityPopup
                    trigger={
                        <Icon link className='Profile__controls fas fa-phone' />
                    }
                    open={openForm === 'call'}
                    onOpen={() => setOpenForm('call')}
                    onClose={onCloseForm}
                    closeOnDocumentClick={false}
                    on="click"
                    position="bottom left"
                    pinned
                    basic
                    wide
                />
            }
            <TextIcon 
                account_id={account_id}
                isActivityFormOpen={openForm === 'text'}
                onCloseActivityForm={onCloseForm}
                onOpenActivityForm={() => setOpenForm('text')}
                onOpenTextModal={() => setOpenForm('text-modal')}
                phone={phone}
                iconClassName={'Profile__controls'}
            />
            <EmailIcon 
                account_id={account_id}
                isActivityFormOpen={openForm === 'email'}
                onClose={onCloseForm}
                onOpenEmailModal={() => setOpenForm('email-modal')}
                hasEmail={hasEmail}
                emailIconClassName={'Profile__controls'}
                isEmailModalOpen={openForm === 'email-modal'}
                onOpenActivityPopup={() => setOpenForm('email')}
            />
            <Icon
                as={NavLink}
                to={`/${locationParam()}/notes`}
                link
                className='Profile__controls fa-sharp fa-solid fa-pen-clip'
                color='grey'
            />
            {account_type === 'business' && (
                <Popup
                    open={openForm === 'unlink-master'}
                    position='bottom center'
                    context={popupRef}
                    onClose={onCloseForm}
                    content={(
                        <Form loading={accountBeingUnlinked}>
                            <ConfirmationPrompt
                                header={<div style={{ paddingTop: '1em' }}>Are you sure?</div>}
                                subheader={<div style={{ marginBottom: '-1.5em' }}>Unlinking removes data from the Master Account.</div>}
                                onConfirm={async () => {
                                    try {
                                        await dispatch(masterAccountActions.unlinkAccount(master_account, null, null, account_id));
                                        Notification.alert('Successfully unlinked account from master account!', STATUS_TYPES.SUCCESS);
                                        onCloseForm();
                                        refresh();
                                    } catch (e) {
                                        console.log(e);
                                        Notification.alert('Failed to unlink account from master account', STATUS_TYPES.ERROR)
                                    }
                                }}
                                onCancel={onCloseForm}
                            />
                        </Form>
                    )}
                />
            )}
            <Popup
                open={openForm === 'add-pipeline'}
                position='bottom center'
                context={popupRef}
                onClose={onCloseForm}
                content={(
                    <>
                        <Header color='primary'>Add Pipeline</Header>
                        <CloseableContent onClose={onCloseForm}>
                            <Form style={{ width: '30em' }} loading={isUpdatingPipeline}>
                                <CustomerPipelineForm primary_contact_id={primary_contact_id} refresh={refresh} account_id={account_id} onClose={onCloseForm} />
                            </Form>
                        </CloseableContent>
                    </>
                )}
            />
            <Popup
                open={openForm === 'close-pipeline' || openForm === 'change-pipeline'}
                position='bottom right'
                context={popupRef}
                onClose={() => {
                    if (openForm === "add-pipeline") {
                        onCloseForm();
                    }
                }}
                content={(
                    <>
                        <Header color='primary'>
                            {`${openForm === 'close-pipeline' ? 'Close' : 'Change'} Pipeline`}
                        </Header>
                        <CloseableContent onClose={onCloseForm}>
                            {openForm === 'close-pipeline' ? (
                                <LostForm
                                    account_id={account_id}
                                    onClose={onCloseForm}
                                    type={'pipeline'}
                                    account_stage={'customer'}
                                    refresh={refresh}
                                    pipeline_id={pipeline?.pipeline_id}
                                />
                            ) : (
                                <ChangeCustomerPipelineForm
                                    account_id={account_id}
                                    onClose={onCloseForm}
                                    refresh={refresh}
                                    current_pipeline_category={pipeline?.pipeline_category_id}
                                    pipeline_id={pipeline?.pipeline_id}
                                />
                            )}
                        </CloseableContent>
                    </>
                )}
            />
            {account_type === 'business' && (
                <Modal open={openForm === 'link-master'} onClose={onCloseForm}>
                    <LinkToMasterAccountForm
                        account_id={account_id}
                        refreshFromLinking={() => {
                            dispatch(accountActions.readAccount(account_id));
                        }}
                        close={onCloseForm}
                        current_master_account={master_account}
                    />
                </Modal>
            )}
             <TextMessageModal
                open={openForm === 'text-modal'}
                onClose={onCloseForm}
                contact_name={account_name}
                contact_id={account_id}
                isAccount
            />
        </React.Fragment>
    );
};

export default Controls;

